import { useCallback, useEffect, useRef } from 'react';

type UseFullscreenAutoHideProps = {
  isFullScreen: boolean;
  containerRef: React.RefObject<HTMLElement>;
  childrenRefs: React.RefObject<HTMLElement>[];
  timeoutMs?: number;
};

export const useFullscreenAutoHide = ({
  isFullScreen,
  containerRef,
  childrenRefs,
  timeoutMs = 2000,
}: UseFullscreenAutoHideProps) => {
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

  const hideChildren = useCallback(() => {
    // Don't hide children in non-fullscreen mode
    if (!isFullScreen) {
      return;
    }

    childrenRefs.forEach((ref) => {
      if (ref.current) {
        ref.current.style.opacity = '0';
        ref.current.style.pointerEvents = 'none';
      }
    });
  }, [isFullScreen, childrenRefs]);

  const showChildren = useCallback(() => {
    childrenRefs.forEach((ref) => {
      if (ref.current) {
        ref.current.style.opacity = '1';
        ref.current.style.pointerEvents = 'all';
      }
    });
  }, [childrenRefs]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    showChildren();
    // Only create timer in fullscreen mode
    if (isFullScreen) {
      inactivityTimerRef.current = setTimeout(hideChildren, timeoutMs);
    }
  }, [showChildren, hideChildren, isFullScreen, timeoutMs]);

  // Handle mouse events
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = resetInactivityTimer;
    const handleMouseEnter = resetInactivityTimer;
    const handleClick = resetInactivityTimer;

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('click', handleClick);

    // Start initial timer only in fullscreen mode
    if (isFullScreen) {
      resetInactivityTimer();
    } else {
      // Just show children without starting a timer
      showChildren();
    }

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('click', handleClick);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [resetInactivityTimer, showChildren, isFullScreen, containerRef]);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  // Ensure children are visible in non-fullscreen mode
  useEffect(() => {
    if (!isFullScreen) {
      clearInactivityTimer();
      showChildren();
    }
  }, [isFullScreen, showChildren, clearInactivityTimer]);

  useEffect(() => {
    return () => {
      clearInactivityTimer();
    };
  }, [clearInactivityTimer]);

  return {
    showChildren,
    hideChildren,
    resetInactivityTimer,
    // Aliases for backward compatibility
    showControls: showChildren,
    hideControls: hideChildren,
  };
};
